import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Clock, Trash } from 'lucide-react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../context/AuthContext';
import {
  SettingsSection,
  SettingsRow,
  SettingsHeader,
  StatusBadge
} from './SettingsUI';

const SystemSettings = () => {
  const [serviceStatuses, setServiceStatuses] = useState({});
  const [scheduledRestarts, setScheduledRestarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const { apiRequest } = useAuth();

  const services = [
    { id: 'zigbee2mqtt', name: 'Zigbee2MQTT', description: 'Zigbee device communication' },
    { id: 'frigate', name: 'Frigate NVR', description: 'Camera recording and detection' },
    { id: 'slzb-06', name: 'SLZB-06 Adapter', description: 'Zigbee coordinator adapter' },
    { id: 'mqtt', name: 'MQTT Broker', description: 'Message broker for IoT' },
    { id: 'caddy', name: 'Caddy Server', description: 'Web server and reverse proxy' }
  ];

  const scheduleOptions = [
    { value: 'daily', label: 'Daily at 3 AM' },
    { value: 'weekly', label: 'Weekly (Sunday 3 AM)' },
    { value: 'monthly', label: 'Monthly (1st at 3 AM)' }
  ];

  const fetchServiceStatuses = useCallback(async () => {
    try {
      const {data} = await apiRequest('/api/system/services/status');
      if (data.success) {
        setServiceStatuses(data.services);
      }
    } catch (error) {
      console.error('Failed to fetch service statuses:', error);
    }
  }, [apiRequest]);

  const fetchScheduledRestarts = useCallback(async () => {
    try {
      const {data} = await apiRequest('/api/system/services/schedule');
      if (data.success) {
        setScheduledRestarts(data.schedules);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled restarts:', error);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchServiceStatuses();
    fetchScheduledRestarts();
  }, [fetchServiceStatuses, fetchScheduledRestarts]);

  const restartService = async (serviceId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {data} = await apiRequest(`/api/system/services/${serviceId}/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (data.success) {
        setSuccess(`${serviceId} restarted successfully`);
        setTimeout(() => fetchServiceStatuses(), 2000);
      } else {
        setError(data.message || 'Failed to restart service');
      }
    } catch (error) {
      setError(`Failed to restart ${serviceId}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const scheduleRestart = async (serviceId, schedule) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {data} = await apiRequest('/api/system/services/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: serviceId, schedule })
      });

      if (data.success) {
        setSuccess(`Periodic restart scheduled for ${serviceId}`);
        fetchScheduledRestarts();
        setSelectedService(null);
      } else {
        setError(data.message || 'Failed to schedule restart');
      }
    } catch (error) {
      setError(`Failed to schedule restart: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeScheduledRestart = async (serviceId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {data} = await apiRequest(`/api/system/services/${serviceId}/schedule`, {
        method: 'DELETE'
      });

      if (data.success) {
        setSuccess(`Scheduled restart removed for ${serviceId}`);
        fetchScheduledRestarts();
      } else {
        setError(data.message || 'Failed to remove scheduled restart');
      }
    } catch (error) {
      setError(`Failed to remove scheduled restart: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-4 space-y-6">
      <SettingsHeader 
        title="System Services" 
        description="Monitor and manage running services"
      />

      {error && (
        <div className="px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {success && (
        <div className="px-4">
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        </div>
      )}

      <SettingsSection title="Services">
        {services.map(service => {
          const status = serviceStatuses[service.id];
          const isScheduled = scheduledRestarts.some(s => s.service === service.id);

          return (
            <SettingsRow
              key={service.id}
              label={service.name}
              description={service.description}
              accessory={
                <div className="flex items-center gap-2">
                  {isScheduled && <Clock className="w-4 h-4 text-primary" />}
                  <StatusBadge status={status?.status || 'unknown'} />
                </div>
              }
              chevron
              onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
            />
          );
        })}
      </SettingsSection>

      {selectedService && (
        <div className="px-4 space-y-3">
          <div className="p-4 bg-card rounded-xl border border-border/60 space-y-3">
            <p className="text-sm font-medium">
              {services.find(s => s.id === selectedService)?.name}
            </p>
            
            <Button
              onClick={() => restartService(selectedService)}
              disabled={loading}
              className="w-full"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? 'Restarting...' : 'Restart Now'}
            </Button>

            {scheduledRestarts.some(s => s.service === selectedService) ? (
              <Button
                variant="outline"
                onClick={() => removeScheduledRestart(selectedService)}
                disabled={loading}
                className="w-full"
                size="sm"
              >
                <Trash className="w-4 h-4 mr-2" />
                Remove Schedule
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Schedule periodic restart:</p>
                <div className="flex gap-2 flex-wrap">
                  {scheduleOptions.map(option => (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      onClick={() => scheduleRestart(selectedService, option.value)}
                      disabled={loading}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {scheduledRestarts.length > 0 && (
        <SettingsSection title="Scheduled Restarts">
          {scheduledRestarts.map((schedule, index) => (
            <SettingsRow
              key={index}
              label={schedule.service}
              description={schedule.cronExpression}
              accessory={
                <button
                  onClick={() => removeScheduledRestart(schedule.service)}
                  disabled={loading}
                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
                >
                  <Trash className="w-4 h-4" />
                </button>
              }
            />
          ))}
        </SettingsSection>
      )}
    </div>
  );
};

export default SystemSettings;
